import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';

const TENANT_SLUGS = ['tcn', 'black'];

/**
 * Catalogue view of `public.metric_formulas`.
 * Read-only metadata + editable `tenant_scopes` toggle (TCN / Generic).
 *
 * Filters:
 *   - search by metric_name
 *   - filter by module
 *   - filter by tenant scope
 */
const IndicatorsRegistryTab = () => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [savingMetric, setSavingMetric] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: dbError } = await supabase
        .from('metric_formulas')
        .select('*')
        .order('module', { ascending: true })
        .order('metric_name', { ascending: true });
      if (cancelled) return;
      if (dbError) setError(dbError.message);
      setRows(data || []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const modules = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.module && set.add(r.module));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (moduleFilter && r.module !== moduleFilter) return false;
      if (tenantFilter && !(r.tenant_scopes || []).includes(tenantFilter)) return false;
      if (!q) return true;
      return (
        r.metric_name?.toLowerCase().includes(q) ||
        r.label_fr?.toLowerCase().includes(q) ||
        r.label_en?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
      );
    });
  }, [rows, search, moduleFilter, tenantFilter]);

  const toggleTenantScope = async (metric, tenantSlug) => {
    const current = new Set(metric.tenant_scopes || []);
    if (current.has(tenantSlug)) current.delete(tenantSlug);
    else current.add(tenantSlug);
    const next = Array.from(current);
    setSavingMetric(metric.metric_name);
    setError(null);
    try {
      const { error: dbError } = await supabase
        .from('metric_formulas')
        .update({ tenant_scopes: next })
        .eq('metric_name', metric.metric_name);
      if (dbError) throw dbError;
      setRows((prev) =>
        prev.map((r) =>
          r.metric_name === metric.metric_name ? { ...r, tenant_scopes: next } : r
        )
      );
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally {
      setSavingMetric(null);
    }
  };

  const lang = i18n.language?.startsWith('ar')
    ? 'ar'
    : i18n.language?.startsWith('fr')
    ? 'fr'
    : 'en';
  const labelFor = (r) => r[`label_${lang}`] || r.label_en || r.label_fr || '';

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.businessRules.indicators.intro',
          'Read-only catalogue of all platform indicators. Each row points to a target table/column and an SQL function that computes it. Editing formulas requires a DB migration.'
        )}
      </p>

      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}

      {!loading && rows.length === 0 ? (
        <div className="rounded-2xl bg-card border border-dashed border-border p-10 text-center">
          <div className="font-heading font-semibold text-foreground mb-2">
            {t(
              'superadmin.businessRules.indicators.notSeededTitle',
              'Catalogue not yet seeded'
            )}
          </div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {t(
              'superadmin.businessRules.indicators.notSeededBody',
              'The metric_formulas table will be populated in Chantier 4 (alongside the SQL recompute functions). It will list all indicators for the 7 modules.'
            )}
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(
                'superadmin.businessRules.indicators.search',
                'Search by metric name, label, description...'
              )}
              className="flex-1 px-4 py-2 rounded-xl bg-card border border-border text-foreground placeholder-muted-foreground/60 text-base outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
            <select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              className="px-3 py-2 rounded-xl bg-card border border-border text-foreground text-base outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
            >
              <option value="">
                {t(
                  'superadmin.businessRules.indicators.allModules',
                  'All modules'
                )}
              </option>
              {modules.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              className="px-3 py-2 rounded-xl bg-card border border-border text-foreground text-base outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
            >
              <option value="">{t('superadmin.businessRules.indicators.allTenants', 'All tenants')}</option>
              {TENANT_SLUGS.map((s) => (
                <option key={s} value={s}>{s.toUpperCase()}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-12 rounded-xl skeleton" />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl bg-card border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface-2">
                      <th className="text-left px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                        {t(
                          'superadmin.businessRules.indicators.metric',
                          'Metric'
                        )}
                      </th>
                      <th className="text-left px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                        {t(
                          'superadmin.businessRules.indicators.module',
                          'Module'
                        )}
                      </th>
                      <th className="text-left px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                        {t(
                          'superadmin.businessRules.indicators.target',
                          'Target'
                        )}
                      </th>
                      <th className="text-left px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                        {t(
                          'superadmin.businessRules.indicators.outputType',
                          'Type'
                        )}
                      </th>
                      <th className="text-left px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                        {t(
                          'superadmin.businessRules.indicators.label',
                          'Label'
                        )}
                      </th>
                      <th className="text-left px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                        {t(
                          'superadmin.businessRules.indicators.tenants',
                          'Tenants'
                        )}
                      </th>
                      <th className="text-left px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                        {t(
                          'superadmin.businessRules.indicators.active',
                          'Active'
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-12 text-center text-muted-foreground text-sm"
                        >
                          {t(
                            'superadmin.businessRules.indicators.noResults',
                            'No indicators match these filters.'
                          )}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((r) => (
                        <tr
                          key={r.metric_name}
                          className="border-b border-border last:border-b-0 hover:bg-surface-2/50 transition-colors"
                        >
                          <td className="px-3 py-2 font-data text-foreground">
                            {r.metric_name}
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium font-data">
                              {r.module}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground text-xs font-data">
                            {r.target_table}.{r.target_column}
                          </td>
                          <td className="px-3 py-2 text-xs text-muted-foreground font-data">
                            {r.output_type}
                          </td>
                          <td className="px-3 py-2 text-foreground">
                            <div className="font-medium">{labelFor(r)}</div>
                            {r.description && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {r.description}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {TENANT_SLUGS.map((slug) => {
                                const on = (r.tenant_scopes || []).includes(slug);
                                return (
                                  <button
                                    key={slug}
                                    type="button"
                                    onClick={() => toggleTenantScope(r, slug)}
                                    disabled={savingMetric === r.metric_name}
                                    className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full transition-colors border ${
                                      on
                                        ? 'bg-accent/10 text-accent border-accent/30'
                                        : 'bg-muted text-muted-foreground/60 border-transparent'
                                    } ${savingMetric === r.metric_name ? 'opacity-60 cursor-not-allowed' : 'hover:border-accent/50'}`}
                                  >
                                    {slug}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                                r.is_active
                                  ? 'bg-success/10 text-success'
                                  : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {r.is_active
                                ? t('superadmin.active', 'Active')
                                : t('superadmin.inactive', 'Inactive')}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IndicatorsRegistryTab;
